/**
 * Help content registry — one entry per admin page.
 *
 * Each entry mirrors the structure expected by <HelpButton />:
 *   - title, tagline
 *   - quick[]  → drawer cards   { icon, title, body }
 *   - tour[]   → guided slides  { icon, title, body }
 *
 * Icons are Lucide icon component names — resolved at render time.
 * Keep copy concise (~ 3-5 lines per card). Mix résumé + "aller plus loin".
 */

export const HELP_CONTENT = {
  /* ─────────────── Affectations flotte ─────────────── */
  "/admin/fleet-assignments": {
    title: "Affectations flotte",
    tagline: "Qui conduit quel bus, sur quelle tournée, quand ?",
    quick: [
      {
        icon: "CalendarDays",
        title: "Planning hebdomadaire",
        body:
          "Vue par défaut : 1 ligne par bus × 5 jours (Lun→Ven). Chaque cellule affiche le code tournée, les horaires et le chauffeur. Cliquez sur une cellule pour éditer l'affectation.",
      },
      {
        icon: "Bus",
        title: "Vue Bus",
        body:
          "Une carte par véhicule avec son chauffeur principal, sa capacité et un mini-strip 5 jours. Idéal pour voir l'état d'occupation d'un bus en un clin d'œil.",
      },
      {
        icon: "LinkIcon",
        title: "Vue Tournées",
        body:
          "Vue inverse : une tuile par tournée. Permet d'affecter durablement un bus + chauffeur à une tournée pour toute l'année scolaire.",
      },
      {
        icon: "Filter",
        title: "Filtres",
        body:
          "Top-droite : Libres, Conflits, Sans chauffeur, Remplacements et Tournées non affectées. Le panneau « Tournées non affectées » offre des cartes cliquables pour créer une affectation rapidement.",
      },
      {
        icon: "RefreshCw",
        title: "Aller plus loin",
        body:
          "Un remplacement ponctuel se crée sur une plage de dates et bascule automatiquement vers le durable à la fin. Les changements sont temps-réel : toutes les pages se mettent à jour automatiquement.",
      },
    ],
    tour: [
      {
        icon: "CalendarDays",
        title: "1 · Vue d'ensemble de la semaine",
        body:
          "À l'ouverture, vous voyez le planning hebdomadaire : chaque ligne est un bus, chaque colonne un jour. Les cellules colorées indiquent l'état (vert=affecté, ambre=remplacement, rouge=conflit, gris=libre).",
      },
      {
        icon: "ArrowRight",
        title: "2 · Naviguer entre les semaines",
        body:
          "Utilisez « Sem. préc. » et « Sem. suiv. » pour avancer dans le calendrier. « Aujourd'hui » ramène à la semaine en cours.",
      },
      {
        icon: "Filter",
        title: "3 · Filtrer pour aller vite",
        body:
          "Cliquez sur « Conflits » pour repérer les doublons, « Sans chauffeur » pour les bus orphelins, ou « Tournées non affectées » pour distribuer les tournées qui manquent.",
      },
      {
        icon: "MousePointer2",
        title: "4 · Éditer en un clic",
        body:
          "Cliquez sur une cellule occupée : le formulaire d'édition s'ouvre. Vous pouvez changer le véhicule, le chauffeur, créer un remplacement ponctuel ou supprimer l'affectation.",
      },
      {
        icon: "Sparkles",
        title: "5 · Affectation rapide depuis un orphelin",
        body:
          "Filtre « Tournées non affectées » → cliquez sur une carte ambre → choisissez bus + chauffeur. L'affectation est immédiatement visible par toute l'équipe.",
      },
    ],
  },

  /* ─────────────── Planning Bus ─────────────── */
  "/admin/dispatch": {
    title: "Planning Bus",
    tagline: "Timeline live des bus en service aujourd'hui.",
    quick: [
      {
        icon: "Radio",
        title: "Timeline temps réel",
        body:
          "Chaque ligne = un bus. Les blocs colorés sont les missions du jour, positionnées sur l'axe horaire. Les positions GPS, l'état moteur et les retards remontent automatiquement.",
      },
      {
        icon: "AlertCircle",
        title: "Alertes & retards",
        body:
          "Un bloc rouge signale un retard ou un incident. Cliquez sur le bloc pour voir les détails et joindre le chauffeur.",
      },
      {
        icon: "Bus",
        title: "Bus inactifs",
        body:
          "Les bus sans mission sont regroupés en bas. Faites-les glisser sur une mission orpheline pour les affecter (à venir).",
      },
      {
        icon: "RefreshCw",
        title: "Auto-refresh",
        body:
          "Les données se rafraîchissent toutes les 5 secondes via WebSocket. Aucune action manuelle nécessaire.",
      },
    ],
    tour: [
      {
        icon: "Radio",
        title: "1 · La timeline",
        body:
          "L'axe horizontal représente la journée (06h→20h par défaut). Chaque ligne est un bus. Les missions s'affichent sous forme de blocs colorés au-dessus de leur créneau horaire.",
      },
      {
        icon: "MapPin",
        title: "2 · Position GPS",
        body:
          "Le marqueur ◆ sur la timeline indique l'heure actuelle. Un bus en mission affiche un curseur de progression réel basé sur sa position GPS.",
      },
      {
        icon: "AlertCircle",
        title: "3 · Gestion d'incident",
        body:
          "Si un bus est en retard, son bloc vire au rouge. Cliquez dessus pour ouvrir le cockpit de mission : statut, ETA, contact chauffeur.",
      },
    ],
  },

  /* ─────────────── Missions ─────────────── */
  "/admin/missions": {
    title: "Missions",
    tagline: "Les missions du jour : pointage chauffeur, scan QR, fiches de route.",
    quick: [
      {
        icon: "ClipboardList",
        title: "Liste & cockpit",
        body:
          "Onglet « Missions » : table de toutes les missions du jour. Cliquez une ligne pour ouvrir le cockpit (détail, scans, GPS, incidents).",
      },
      {
        icon: "FileText",
        title: "Fiches de route (PDF)",
        body:
          "Onglet « Fiches de route » : pour chaque chauffeur, génère le PDF officiel à remettre avant la mission (arrêts, horaires, élèves, signature).",
      },
      {
        icon: "Filter",
        title: "Filtres",
        body:
          "Filtrez par date, chauffeur, véhicule, tournée ou école. Combinables pour zoomer sur un cas précis.",
      },
      {
        icon: "Download",
        title: "Téléchargement",
        body:
          "Chaque fiche se télécharge individuellement. Un bouton « Tout télécharger » (ZIP) arrive bientôt.",
      },
    ],
    tour: [
      {
        icon: "ClipboardList",
        title: "1 · Vue Missions",
        body:
          "Toutes les missions du jour avec leur statut (à venir / en cours / terminée). Cliquez « Cockpit » pour voir les scans QR, le GPS et les incidents en direct.",
      },
      {
        icon: "FileText",
        title: "2 · Vue Fiches de route",
        body:
          "Bascule en haut à droite. Une carte par chauffeur regroupe ses tournées du jour. Téléchargez le PDF ou cliquez « Envoyer » (email/SMS — à venir).",
      },
      {
        icon: "Phone",
        title: "3 · Téléphone d'exploitation",
        body:
          "Le numéro affiché en bas des fiches PDF provient de Paramètres → Téléphone exploitation. Modifiez-le une fois pour qu'il apparaisse sur toutes les futures fiches.",
      },
    ],
  },

  /* ─────────────── Import tournées ─────────────── */
  "/admin/programs": {
    title: "Import tournées (Excel)",
    tagline: "Chargez les fichiers clients pour générer automatiquement les tournées.",
    quick: [
      {
        icon: "FileSpreadsheet",
        title: "Format attendu",
        body:
          "Excel client standard : 1 onglet par tournée, colonnes Arrêt / Heure / Élève / Bus. Le moteur détecte automatiquement les en-têtes (codes service, jours, horaires).",
      },
      {
        icon: "Sparkles",
        title: "Auto-affectation véhicule",
        body:
          "Le service `vehicle_matcher` lit la plaque/code bus dans l'Excel et crée automatiquement l'affectation durable côté Logibus.",
      },
      {
        icon: "AlertCircle",
        title: "Lignes « À confirmer »",
        body:
          "Les lignes ambiguës (arrêt inconnu, horaire incohérent) sont marquées « À confirmer ». Corrigez-les avant validation.",
      },
      {
        icon: "ArrowRight",
        title: "Cycle complet",
        body:
          "Importer → vérifier → valider → les tournées apparaissent automatiquement dans Affectations + Planning Bus + Missions.",
      },
    ],
    tour: [
      {
        icon: "Upload",
        title: "1 · Déposer le fichier",
        body:
          "Glissez l'Excel client dans la zone de dépôt. Le moteur d'import lance la détection des en-têtes et la création des tournées.",
      },
      {
        icon: "Sparkles",
        title: "2 · Auto-affectation",
        body:
          "Si la colonne « Bus » contient des plaques connues, l'affectation durable est créée d'office. Sinon, une alerte vous invite à corriger.",
      },
      {
        icon: "CheckCircle2",
        title: "3 · Valider l'import",
        body:
          "Vérifiez les lignes « À confirmer » puis cliquez « Valider ». Les tournées sont immédiatement disponibles dans les autres modules.",
      },
    ],
  },

  /* ─────────────── Chauffeurs ─────────────── */
  "/admin/drivers": {
    title: "Chauffeurs",
    tagline: "Gérez les chauffeurs et leurs assignations.",
    quick: [
      {
        icon: "UserCog",
        title: "Liste & filtres",
        body:
          "Recherchez par nom, email ou téléphone. Filtrez par statut (actif / désactivé) ou par véhicule principal.",
      },
      {
        icon: "ShieldCheck",
        title: "Désactivation = cascade",
        body:
          "Désactiver un chauffeur le retire automatiquement de toutes ses affectations actives (durables + remplacements). Aucune trace fantôme.",
      },
      {
        icon: "Radio",
        title: "Sync Logitrak",
        body:
          "Les chauffeurs proviennent de Logitrak (GPS). Pour les ajouter/modifier, utilisez le portail Logitrak puis « Sync Logitrak » côté Logibus.",
      },
      {
        icon: "Phone",
        title: "Téléphone & email",
        body:
          "Les coordonnées sont utilisées par les fiches de route et les notifications. Renseignez-les soigneusement.",
      },
    ],
    tour: [
      {
        icon: "UserCog",
        title: "1 · Liste des chauffeurs",
        body:
          "Tableau récapitulatif avec statut, téléphone, véhicule principal et nombre de tournées affectées cette semaine.",
      },
      {
        icon: "Radio",
        title: "2 · Synchronisation Logitrak",
        body:
          "Cliquez « Sync Logitrak » pour rapatrier les nouveaux chauffeurs et leurs pairings. Logibus reste maître pour les affectations de tournées.",
      },
      {
        icon: "ShieldAlert",
        title: "3 · Désactiver proprement",
        body:
          "Le bouton « Désactiver » annule en cascade toutes les affectations en cours. À utiliser dès qu'un chauffeur quitte l'équipe.",
      },
    ],
  },

  /* ─────────────── Calendrier scolaire ─────────────── */
  "/admin/calendar": {
    title: "Calendrier scolaire",
    tagline: "Vacances, jours fériés et journées spéciales.",
    quick: [
      {
        icon: "CalendarDays",
        title: "Périodes scolaires",
        body:
          "Définissez les vacances, jours fériés et journées avec horaires spéciaux. Le système n'affecte pas de bus pendant ces périodes.",
      },
      {
        icon: "AlertCircle",
        title: "Impact sur Affectations",
        body:
          "Les affectations durables sont automatiquement suspendues pendant les vacances. Pas besoin de les supprimer manuellement.",
      },
      {
        icon: "Sparkles",
        title: "Modèles canton VD",
        body:
          "Préchargez le calendrier officiel du canton de Vaud en un clic. Vous pouvez ensuite l'ajuster pour votre école.",
      },
    ],
    tour: [
      {
        icon: "CalendarDays",
        title: "1 · Ajouter une période",
        body:
          "Cliquez « + Période », saisissez début/fin et catégorie (vacances, férié, journée spéciale). Validez : c'est appliqué partout.",
      },
      {
        icon: "Sparkles",
        title: "2 · Importer le canton VD",
        body:
          "Bouton « Charger calendrier VD » : récupère automatiquement les vacances officielles. Modifiables ensuite.",
      },
      {
        icon: "RefreshCw",
        title: "3 · Effets",
        body:
          "Affectations, Planning Bus et Missions tiennent compte du calendrier en temps réel. Les jours marqués sont grisés dans toutes les vues.",
      },
    ],
  },

  /* ═════════════════════════════════════════════════════════════════
     CÔTÉ CHAUFFEUR
     ═════════════════════════════════════════════════════════════════ */

  /* ─────────────── Driver · Mes missions ─────────────── */
  "/driver": {
    title: "Mes missions (Chauffeur)",
    tagline: "Vos tournées du jour, le scan QR et le suivi GPS.",
    quick: [
      { icon: "ClipboardList", title: "Tournées du jour",
        body: "Au lancement, vous voyez toutes vos tournées d'aujourd'hui dans l'ordre chronologique. Une bande jaune à gauche indique celle en cours." },
      { icon: "MapPin", title: "Détail d'une tournée",
        body: "Touchez une tournée pour voir la liste des arrêts, les horaires et les élèves attendus. Bouton « Démarrer » en haut pour lancer la mission." },
      { icon: "Radio", title: "GPS automatique",
        body: "Dès que vous démarrez une mission, votre position est envoyée toutes les 5 secondes. Aucune action manuelle — gardez juste l'écran ouvert." },
      { icon: "QrCode", title: "Scan QR enfant",
        body: "À chaque arrêt, scannez le QR code de l'élève (caméra ou code manuel). « Embarquement » à l'aller, « Débarquement » à l'arrivée." },
      { icon: "AlertCircle", title: "Signaler un incident",
        body: "En cas de retard, panne, accident ou absence d'élève : touchez « Signaler un incident » sur le détail. L'admin est notifié instantanément." },
      { icon: "CheckCircle2", title: "Terminer la mission",
        body: "Quand tous les élèves sont déposés, touchez « Terminer ». La mission passe à « terminée » et l'admin reçoit la confirmation horodatée." },
    ],
    tour: [
      { icon: "ClipboardList", title: "1 · Démarrez votre journée",
        body: "Ouvrez LOGIBUS le matin. Toutes vos tournées du jour sont listées avec horaires et nombre d'arrêts. Touchez la première pour commencer." },
      { icon: "ArrowRight", title: "2 · Démarrer la mission",
        body: "Sur l'écran détail, touchez « Démarrer ». Le GPS s'active automatiquement et l'admin voit votre position en temps réel sur la timeline." },
      { icon: "QrCode", title: "3 · Scanner les enfants",
        body: "À chaque arrêt, ouvrez la caméra et scannez le QR de l'élève (ou saisissez le code à la main si la caméra ne marche pas). Le scan est confirmé visuellement." },
      { icon: "AlertCircle", title: "4 · Gérer un imprévu",
        body: "Bouton « Signaler un incident » en bas : choisissez le type (retard, panne, absence, accident), ajoutez un commentaire et envoyez. L'admin reçoit l'alerte immédiatement." },
      { icon: "RefreshCw", title: "5 · Mode hors-ligne",
        body: "Pas de réseau ? Les scans et incidents sont mis en file d'attente et envoyés dès que la connexion revient. L'icône Wifi en haut à droite indique le statut." },
      { icon: "CheckCircle2", title: "6 · Terminer & enchaîner",
        body: "À la fin, touchez « Terminer » : la mission passe à l'état « terminée ». La tournée suivante apparaît automatiquement en tête de liste." },
    ],
  },

  /* ─────────────── Driver · Scan manuel ─────────────── */
  "/driver/scan": {
    title: "Scan QR (Chauffeur)",
    tagline: "Scanner un enfant rapidement, même sans mission ouverte.",
    quick: [
      { icon: "QrCode", title: "Caméra ou code manuel",
        body: "Deux modes : (1) la caméra arrière scanne automatiquement le QR, (2) saisissez le code à la main si la caméra n'est pas dispo (rare)." },
      { icon: "ArrowRight", title: "Type de scan",
        body: "Choisissez « Embarquement » (montée le matin) ou « Débarquement » (descente le soir) avant de scanner. Le système enregistre l'heure exacte." },
      { icon: "MapPin", title: "Position incluse",
        body: "Chaque scan enregistre votre GPS. L'admin et les parents savent où l'enfant a été pris en charge." },
      { icon: "AlertCircle", title: "Hors-ligne ?",
        body: "Aucun problème : les scans sont stockés et synchronisés dès que le réseau revient. Le compteur en haut à droite indique combien sont en attente." },
    ],
    tour: [
      { icon: "QrCode", title: "1 · Sélectionner le mode",
        body: "En haut de l'écran : choisissez « Embarquement » (matin) ou « Débarquement » (soir). Couleur jaune = mode actif." },
      { icon: "Sparkles", title: "2 · Ouvrir la caméra",
        body: "Touchez « Activer caméra » : autorisez l'accès la première fois. Pointez le QR de l'enfant — le scan est automatique avec un petit bip." },
      { icon: "CheckCircle2", title: "3 · Confirmation",
        body: "Le nom de l'élève apparaît à l'écran pendant 2 secondes. Vous pouvez enchaîner les scans sans manipulation supplémentaire." },
    ],
  },

  /* ─────────────── Driver · Détail mission ─────────────── */
  "/driver/mission": {
    title: "Détail mission (Chauffeur)",
    tagline: "Conduite de la mission étape par étape.",
    quick: [
      { icon: "ArrowRight", title: "Démarrer / Terminer",
        body: "Bouton vert « Démarrer » en haut → active le GPS et le suivi. À la fin, bouton « Terminer » → la mission passe à « terminée »." },
      { icon: "MapPin", title: "Carte temps réel",
        body: "La carte affiche les arrêts dans l'ordre + votre position GPS. Les arrêts déjà visités passent en vert." },
      { icon: "UserCog", title: "Liste élèves",
        body: "Sous la carte : liste des enfants attendus avec statut (à embarquer, à bord, déposé). Touchez un nom pour le scanner directement." },
      { icon: "QrCode", title: "Scan rapide",
        body: "Le gros bouton « Scanner » au centre ouvre la caméra. Le mode (embarquement/débarquement) se déduit automatiquement de l'arrêt en cours." },
      { icon: "AlertCircle", title: "Incident & absence",
        body: "Bouton « Incident » en bas : choisissez le type. Bouton « Absent » sur un élève : marque-le absent sans pénaliser la mission." },
    ],
    tour: [
      { icon: "ArrowRight", title: "1 · Démarrer",
        body: "Touchez « Démarrer la mission » en haut. Le GPS s'active, l'admin voit votre position et la mission passe au statut « en cours »." },
      { icon: "MapPin", title: "2 · Suivre les arrêts",
        body: "La carte vous guide arrêt par arrêt. À chaque arrêt, scannez les enfants présents. Les arrêts effectués deviennent verts automatiquement." },
      { icon: "QrCode", title: "3 · Scanner",
        body: "Touchez le bouton « Scanner » : la caméra s'ouvre. Approchez le QR — bip = scan validé. Plusieurs enfants ? Enchaînez sans fermer la caméra." },
      { icon: "ShieldAlert", title: "4 · Élève absent",
        body: "Un enfant manque ? Touchez son nom dans la liste → « Marquer absent ». Le parent et l'admin sont notifiés. Aucun blocage pour continuer la mission." },
      { icon: "AlertCircle", title: "5 · Incident en cours de route",
        body: "Panne, retard, accident ? Touchez « Signaler un incident », choisissez le type, ajoutez un commentaire texte. Envoyé en moins de 3 secondes." },
      { icon: "CheckCircle2", title: "6 · Terminer",
        body: "Tous les enfants déposés ? Touchez « Terminer la mission ». La fiche se ferme, le GPS s'arrête, l'admin reçoit la confirmation horodatée." },
    ],
  },
};

/** Returns the help entry for a given pathname, or null if none. */
export function getHelpForPath(pathname) {
  if (!pathname) return null;
  // Exact match first
  if (HELP_CONTENT[pathname]) return HELP_CONTENT[pathname];
  // Prefix match (e.g. /admin/missions/123 → /admin/missions)
  const keys = Object.keys(HELP_CONTENT).sort((a, b) => b.length - a.length);
  for (const k of keys) {
    if (pathname.startsWith(k + "/")) return HELP_CONTENT[k];
  }
  return null;
}